import React, { useMemo, useState } from 'react';
import { TableSummaryByTasks, TableSummaryBySchool } from '../Table';
import { Olympiad, OlympiadStatus } from '../../model';
import { OlympiadsApi } from 'api';
import FileSaver from 'file-saver';

import { Space, Text } from '@gr-monorepo/common/ui';
import OlympiadDetailsUi from './OlympiadDetailsUi';
import DownloadReportButton from './DownloadReportButton';
import { TableTitle, TitleStatistics } from './OlympiadDetailsStyled';
import Ratings from './Ratings';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const OlympiadDetails = (props: {
  status: OlympiadStatus;
  isRegionUser: boolean;
  showRating: boolean;
  data: Olympiad;
  userType: string;
  largeCity: boolean;
  tableOnly?: boolean;
  tableWrapper?: React.ComponentType<React.PropsWithChildren>;
  hideTableTitle?: boolean;
}) => {
  const { id, schools, municipalities, cards, total, description } = props.data;
  const { status, userType } = props;
  const isBkad = ['roads2309', 'roads2211'].includes(id);
  const hideRating = props.data.ratings?.some((el) => el.value > 100);
  const [pendingReport, setPendingReport] = useState(false);
  const tableData = useMemo(
    () => (props.isRegionUser ? municipalities : schools),
    [municipalities, schools, props.isRegionUser],
  );

  const withoutParticipants = !tableData?.some((el) => el.participantsCount > 0);

  const onDownloadReport = () => {
    setPendingReport(true);
    OlympiadsApi.getOlyimpiadReportById(id)
      .then((response: any) => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        FileSaver.saveAs(blob, `Отчет по олимпиадам.xlsx`);
        setPendingReport(false);
      })
      .catch(() => {
        setPendingReport(false);
      });
  };

  const dataActual = dayjs().tz('Europe/Moscow').subtract(2, 'day').endOf('day').format('DD MMMM YYYY г. HH:mm');

  const downloadReportButton =
    status !== OlympiadStatus.future && onDownloadReport ? (
      <DownloadReportButton
        disabled={withoutParticipants}
        pendingReport={pendingReport}
        onDownloadReport={onDownloadReport}
        olympiadId={id}
      />
    ) : null;

  const tableBlock = (
    <>
      <TableSummaryBySchool
        total={total}
        rows={props.isRegionUser ? municipalities : schools}
        status={status}
        isRegionUser={props.isRegionUser}
        __isRoads2211={isBkad}
        olympiadId={props.data.id}
      />
      {status === OlympiadStatus.current && !!description ? (
        <Text>
          После завершения олимпиады вам будет доступна информация, какое
          <br /> задание далось ученикам легче всего, а какое — оказалось самым сложным
        </Text>
      ) : status === OlympiadStatus.passed && cards?.length ? (
        <TableSummaryByTasks rows={cards} />
      ) : null}
      {downloadReportButton}
    </>
  );

  const detailsContent = (
    <>
      {props.showRating && (
        <>
          <Space axis="vertical" size={12} />
          {!hideRating ? (
            <>
              <Ratings
                olympiadId={props.data.id}
                data={props.data.ratings!}
                informationLink={props.data.url!}
                isPassedOlympiad={status === OlympiadStatus.passed}
                popupText={props.data.ratingStaticData?.popup_text || null}
                hasValueByRealStudentsCount={props.data.ratingByRealStudentsCount}
              />
              <Space axis="vertical" size={40} />
            </>
          ) : (
            <Space axis="vertical" size={20} />
          )}
          {!props.hideTableTitle && (
            <TableTitle>
              <div>
                <div>
                  Статистика активности учащихся с 1 по 11 класс в школах муниципалитета{' '}
                  {status === OlympiadStatus.current && <span>{`на ${dataActual}  по мск`}</span>}
                </div>
              </div>
            </TableTitle>
          )}
        </>
      )}
      {status === OlympiadStatus.current && !props.showRating && !props.hideTableTitle && (
        <>
          <Space axis="vertical" size={12} />
          <TitleStatistics>
            Статистика активности учащихся с 1 по 11 класс в школах{' '}
            {userType === 'citydistrict' || props.largeCity ? 'муниципалитета' : 'региона'} на {dataActual}&nbsp;пo&nbsp;мск
          </TitleStatistics>
        </>
      )}
      {props.tableOnly && props.tableWrapper ? (
        <props.tableWrapper>{tableBlock}</props.tableWrapper>
      ) : (
        tableBlock
      )}
    </>
  );

  if (props.tableOnly) {
    return detailsContent;
  }

  return (
    <OlympiadDetailsUi
      status={status}
      data={props.data}
      showRating={props.showRating}
      pendingReport={pendingReport}
      onDownloadReport={onDownloadReport}
      withoutParticipants={withoutParticipants}
    >
      {detailsContent}
    </OlympiadDetailsUi>
  );
};

export default OlympiadDetails;
